import appContext from 'context/appContext'
import { useContext } from 'react'
import { ApiResponse } from './useApi'

interface useInitialiserProps {
  subscribedApis?: ApiResponse<any>[]
}

/**
 * A hook to determine the initialisation state of the application and extend it with the initialisation state of additional APIs called on the specific page
 * Initialisation state is determined by the INITIAL loading state of core APIs such as "clients", "config", "permissions" and any additional APIs passed in as props
 * Subsequent loading states of the APIs are not considered initialisation
 *
 * @param param0 The configuration object
 * @returns whether the application/page is still initialising
 *
 * @example
 * const singleProjectApiMethods = useApi<ProjectsDetailResult>(`/projects/${projectId}`)
 * const { isInitialising } = useInitialiser({ subscribedApis: [ singleProjectApiMethods ] })
 * if (isInitialising) return <FormSkeleton />
 */
const useInitialiser = ({ subscribedApis }: useInitialiserProps) => {
  const { clientsApiMethods, getPermissionsApiClient, configApi } = useContext(appContext)

  const checkIsInitialising = ({ isLoading, data, error }: ApiResponse<any>) =>
    (isLoading && !data) || (!data && !error)

  const coreInitialisationParams =
    checkIsInitialising(clientsApiMethods) ||
    checkIsInitialising(getPermissionsApiClient) ||
    checkIsInitialising(configApi)

  // Check if any of the subscribed APIs are still initialising
  const subscribedApisInitialisationStatuses = subscribedApis?.map(checkIsInitialising) || []

  const isInitialising = coreInitialisationParams || subscribedApisInitialisationStatuses.some(Boolean)

  return { coreInitialisationParams, isInitialising }
}

export default useInitialiser
